/**
 * deductions — the non-component half of the deduction surfaces: the accepted
 * kinds, the labels, and the draft<->wire helpers the record and correction
 * gates share. The two COMPONENTS live in DeductionFields.tsx, split out purely
 * because react-refresh requires a module to export components or values, not
 * both.
 *
 * WHY THE KIND LIST IS DUPLICATED HERE AT ALL. The authority is the database
 * CHECK `payroll_period_deductions_kind_allowed` (0081, narrowed by 0082), and
 * pkg/contracts.DeductionKinds is its Go mirror; this is a THIRD copy and every
 * copy is a drift risk. It exists because a picklist is the only thing that
 * stops an operator submitting a kind at all — and it is deliberately a
 * PICKLIST rather than a free-text box, so the client cannot invent a value the
 * CHECK will refuse. The server remains the judge: if this list ever drifts
 * ahead of the CHECK, the named 400 below is what the operator sees, and it
 * carries the server's own accepted-kinds sentence rather than anything from
 * here. Keep this list in the CHECK's order, and change it only alongside a
 * migration.
 *
 * give_as_you_earn is DELIBERATELY ABSENT. 0082 removed it from the CHECK
 * because HMRC's RTI Data Item Guide puts payroll giving under item 62 (subject
 * to Class 1 NIC but NOT taxed under PAYE) rather than item 58B (deductions
 * from net pay) — it reduces TAXABLE pay, so it must reach the PAYE engine as
 * an input rather than being subtracted afterwards. The server still refuses it
 * BY NAME with that explanation, which is a different message from the generic
 * unknown-kind refusal, and the gates render both faithfully rather than
 * collapsing them into one "invalid kind".
 */
import type { PayrollDeduction } from '../../types'

/** The five accepted kinds, in the CHECK's order. */
export const DEDUCTION_KINDS = [
  'pension_relief_at_source',
  'attachment_of_earnings',
  'trade_union',
  'loan_repayment',
  'other',
] as const

export const KIND_LABEL: Record<string, string> = {
  pension_relief_at_source: 'Pension (relief at source)',
  attachment_of_earnings: 'Attachment of earnings',
  trade_union: 'Trade union',
  loan_repayment: 'Loan repayment',
  other: 'Other',
}

/** An unknown kind renders VERBATIM rather than being hidden or relabelled: a
 *  stored row whose kind this build does not know about is data, and silently
 *  showing nothing would be the dishonest option. */
export function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind
}

export const FIXED_OR_VARIABLE = ['fixed', 'variable'] as const

export const FIXED_OR_VARIABLE_LABEL: Record<string, string> = {
  fixed: 'Fixed', variable: 'Variable',
}

/** Σ of a list's amounts. Used ONLY for figures the wire does not already
 *  carry — namely the editor's running total of what is about to be SENT. A
 *  stored row's total is read from otherDeductionsTotalPence, never re-added
 *  here, so the browser and the server cannot disagree about a written figure. */
export function deductionsTotalPence(ds: PayrollDeduction[]): number {
  return ds.reduce((sum, d) => sum + d.amountPence, 0)
}

/** A deduction row mid-edit. Amount is held as the raw STRING the operator
 *  typed, exactly as gross is on both gates: parsing on every keystroke would
 *  fight the input (a half-typed "1." is not a number yet). */
export type DeductionDraft = {
  kind: string
  purpose: string
  amount: string
  fixedOrVariable: string
}

export function emptyDraft(): DeductionDraft {
  return { kind: DEDUCTION_KINDS[0], purpose: '', amount: '', fixedOrVariable: FIXED_OR_VARIABLE[0] }
}

export function draftFromDeduction(d: PayrollDeduction): DeductionDraft {
  return {
    kind: d.kind,
    purpose: d.purpose,
    amount: String(d.amountPence / 100),
    fixedOrVariable: d.fixedOrVariable,
  }
}

/** A draft is complete when every field the server requires is present and the
 *  amount parses. purpose and fixedOrVariable are REQUIRED — purpose because
 *  s.8(2)(b) makes it the particular that gives the figure meaning, and an
 *  unlabelled deduction on a payslip is exactly what the statute forbids. */
export function draftComplete(d: DeductionDraft): boolean {
  const n = Number(d.amount)
  return d.purpose.trim() !== '' &&
    d.kind !== '' &&
    d.fixedOrVariable !== '' &&
    d.amount !== '' && Number.isFinite(n) && n >= 0
}

export function draftsValid(ds: DeductionDraft[]): boolean {
  return ds.every(draftComplete)
}

/** Drafts → wire shape. Callers MUST gate on draftsValid first; this does not
 *  filter incomplete rows, because dropping a row the operator can see would
 *  send something other than what is on screen. */
export function draftsToDeductions(ds: DeductionDraft[]): PayrollDeduction[] {
  return ds.map(d => ({
    kind: d.kind,
    purpose: d.purpose.trim(),
    amountPence: Math.round(Number(d.amount) * 100),
    fixedOrVariable: d.fixedOrVariable,
  }))
}

